import {
    Box,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { useGetRefundChunks, refundBooking } from '@openapi/bookings/bookings';
import SkeletonTable from '../../../../components/atom/SkeletonTable';
import useNotify from '../../../../hooks/useNotify';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { formatCurrency } from '../../helpers/formatters';
import CommonButton from '../../../../components/atom/CommonButton';
import InfoIcon from '@mui/icons-material/Info';

function RefundChunksTable({ orders, bookingId, reloadData, reservationHash }) {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    const navigate = useNavigate();
    const [showInfo, setShowInfo] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadRefundChunks,
    } = useGetRefundChunks(
        Number(bookingId),
        {
            reservationHash,
        },
        {
            query: {
                onError: (err) => {
                    notifyError(err);
                    navigate('/bookings');
                },
            },
        },
    );

    const [rawRefundInputs, setRawRefundInputs] = useState([]);
    const refundInputs = rawRefundInputs.map((value) => {
        const parsedValue = parseFloat(value);
        return isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);
    });

    // Calculate the maximum amount that can be refunded
    let refundingMaxAmount = 0;
    const refundingOrders = [];
    orders.forEach((order) => {
        if (String(order.status) !== 'refunding') {
            return;
        }
        refundingMaxAmount += order.amount * -1; // totalAmount is negative for refunding orders
        order.transactions.forEach((trans) => {
            refundingMaxAmount += trans.amount;
        });
        refundingOrders.push({ id: order.id, amount: order.amount });
    });

    // Hide the block once every refunding order is fully covered by
    // transactions (refundingMaxAmount === 0). Otherwise admins see a $0.00
    // title + an empty table after a successful refund, until the order's
    // REFUNDING → REFUNDED transition propagates. Still show it while
    // loading so the Skeleton has a place to render.
    if (!loading && refundingMaxAmount <= 0) {
        return null;
    }

    // Handles refunding booking chunks
    const handleRefundBooking = async () => {
        const chunks = data?.chunks || [];
        if (!chunks || !Array.isArray(chunks) || chunks.length === 0) {
            alert(t('errors.noChunksSelected'));
            return;
        }
        setIsSubmitting(true);
        try {
            await refundBooking(
                Number(bookingId),
                {
                    orders: refundingOrders,
                    chunks: chunks
                        .map((chunk, idx) => {
                            return {
                                name: chunk.name,
                                // Round, not floor: 1.81 * 100 = 180.99…
                                // in IEEE-754, and floor would submit 180
                                // cents instead of 181 for residual refunds.
                                amount: Math.round(refundInputs[idx] * 100),
                                method: chunk.method,
                                info: chunk.info || {},
                            };
                        })
                        .filter((e) => !Number.isNaN(e.amount)),
                },
                {
                    reservationHash,
                },
            );
            setRawRefundInputs([]); // Reset inputs after successful refund
            reloadData();
            reloadRefundChunks();
        } catch (error) {
            console.error('Error refund booking:', error);
            notifyError(t('errors.refundReservationError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box style={{ textAlign: 'left', width: '100%', position: 'relative' }}>
            <Typography
                variant="h5"
                style={{
                    position: 'absolute',
                    top: '-4rem',
                    right: 0,
                }}
            >
                {formatCurrency(refundingMaxAmount / 100)}
                <IconButton
                    size="small"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => {
                        setShowInfo(!showInfo);
                    }}
                >
                    <InfoIcon />
                </IconButton>
            </Typography>
            <TableContainer>
                <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('refundChunks.columns.name')}</TableCell>
                            <TableCell>{t('refundChunks.columns.max')}</TableCell>
                            {showInfo && <TableCell>{t('refundChunks.columns.info')}</TableCell>}
                            <TableCell>{t('refundChunks.columns.method')}</TableCell>
                            <TableCell>{t('refundChunks.columns.note')}</TableCell>
                            <TableCell>{t('refundChunks.refundAmount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={2} cells={6} />
                        ) : (
                            data?.chunks?.map((chunk, idx) => (
                                <TableRow key={chunk.id ?? idx}>
                                    <TableCell>{chunk.name}</TableCell>
                                    <TableCell>{formatCentsToUsd(chunk.max)}</TableCell>
                                    {showInfo && (
                                        <TableCell>{JSON.stringify(chunk.info, null, 2)}</TableCell>
                                    )}
                                    <TableCell>{chunk.method}</TableCell>
                                    <TableCell>{chunk.note}</TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            label={t('refundChunks.refundAmount')}
                                            type="number"
                                            min={0}
                                            max={chunk.max / 100}
                                            required
                                            value={rawRefundInputs[idx] || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setRawRefundInputs((prev) => {
                                                    const newInputs = [...prev];
                                                    newInputs[idx] = value;
                                                    return newInputs;
                                                });
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        $
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <CommonButton
                fullWidth
                disabled={
                    isSubmitting ||
                    // check if there are any refund inputs with a value greater than 0
                    !Object.values(refundInputs).some((input) => input > 0) ||
                    // check if the total refund amount exceeds the maximum allowed
                    refundInputs.reduce((acc, curr) => acc + curr, 0) * 100 > refundingMaxAmount ||
                    // check if any refund input exceeds the corresponding chunk max
                    data?.chunks?.some((chunk, idx) => {
                        return chunk.max < refundInputs[idx] * 100;
                    })
                }
                label={t('refundChunks.refundButton')}
                buttonType="primary"
                onClick={handleRefundBooking}
            />
        </Box>
    );
}

export default RefundChunksTable;
